import saoPauloImg from "@/assets/cities/sao-paulo.jpg";
import salvadorImg from "@/assets/cities/salvador.jpg";
import florianopolisImg from "@/assets/cities/florianopolis.jpg";
import fozDoIguacuImg from "@/assets/cities/foz-do-iguacu.jpg";

export interface CityData {
  slug: string;
  name: string;
  state: string;
  title: string;
  metaDescription: string;
  intro: string;
  image: string;
  imageAlt: string;
  areas: string[];
  pharmacies: string[];
  audience: string;
  localNotes: string[];
  emergencyNote: string;
}

export const cities: CityData[] = [
  {
    slug: "english-speaking-doctor-sao-paulo",
    name: "São Paulo",
    state: "SP",
    title: "English-Speaking Doctor in São Paulo | Online Telemedicine | Rio Med Care",
    metaDescription:
      "Online consultation in English with a board-certified family doctor for travellers and expats in São Paulo. USD 50, digital prescriptions valid in Brazil.",
    intro:
       "If you are travelling for business or living as an expat in São Paulo, finding an English-speaking doctor can be difficult. Rio Med Care offers online consultations with licensed Brazilian physicians from wherever you are in the city.",
    image: saoPauloImg,
    imageAlt: "Aerial view of Avenida Paulista and the São Paulo skyline at sunset",
    areas: [
      "Avenida Paulista and Bela Vista",
      "Jardins, Itaim Bibi and Vila Olímpia",
      "Pinheiros, Vila Madalena and Faria Lima",
      "Moema and Brooklin",
      "Guarulhos and airport hotels",
    ],
    pharmacies: ["Drogasil", "Droga Raia", "Drogaria São Paulo", "Pague Menos"],
    audience:
      "business travellers, corporate expats, digital nomads and families living in the city",
    localNotes: [
      "Consultations are held by secure video call — you only need a phone or laptop and a stable connection.",
      "When clinically indicated, a digital prescription signed with a Brazilian certified signature (ICP-Brasil) is sent to you and accepted at pharmacies across São Paulo.",
      "Medical reports and receipts are issued in English for international travel insurance reimbursement.",
    ],
    emergencyNote:
      "For chest pain, difficulty breathing, severe bleeding, stroke symptoms or major trauma, call 192 (SAMU) or go to the nearest emergency department — Hospital Israelita Albert Einstein, Hospital Sírio-Libanês and Hospital Samaritano all receive international patients.",
  },
  {
    slug: "english-speaking-doctor-salvador",
    name: "Salvador",
    state: "Bahia",
    title: "English-Speaking Doctor in Salvador, Bahia | Online Telemedicine | Rio Med Care",
    metaDescription:
      "English-speaking board-certified family doctor online while visiting Salvador, Bahia. USD 50, digital prescriptions and insurance receipts in English.",
    intro:
      "Salvador's heat, street food and beaches account for most of the health issues visitors run into: dehydration, sunburn, traveller's diarrhoea and insect bites. Rio Med Care lets you speak with an English-speaking board-certified family physician online, without leaving your hotel or pousada.",
    image: salvadorImg,
    imageAlt: "Colorful colonial houses and baroque churches of Pelourinho, Salvador, overlooking the bay",
    areas: [
      "Barra and Ondina",
      "Pelourinho and Centro Histórico",
      "Rio Vermelho",
      "Itapuã and Stella Maris",
      "Costa do Sauípe and coastal resorts",
    ],
    pharmacies: ["Drogasil", "Pague Menos", "Droga Raia", "Farmácias Independentes"],
    audience: "tourists, cruise passengers, surfers and long-stay visitors in Bahia",
    localNotes: [
      "Heat illness and dehydration are the most common reasons visitors contact us in Bahia — early assessment usually avoids a hospital visit.",
      "Tropical fevers such as dengue, zika and chikungunya are present in the region and are assessed carefully during the consultation.",
      "Digital prescriptions are accepted in pharmacies throughout Salvador and the Bahia coast.",
    ],
    emergencyNote:
      "In an emergency call 192 (SAMU). Hospital Aliança and Hospital Português in Salvador have private emergency departments used by international visitors.",
  },
  {
    slug: "english-speaking-doctor-florianopolis",
    name: "Florianópolis",
    state: "Santa Catarina",
    title: "English-Speaking Doctor in Florianópolis | Online Telemedicine | Rio Med Care",
    metaDescription:
      "Online consultations in English for digital nomads and travellers in Florianópolis. Board-certified family doctor, USD 50, digital prescriptions.",
    intro:
      "Florianópolis has one of the largest digital nomad communities in Brazil, and most long-stay visitors have no local doctor. Rio Med Care provides online consultations in English with a board-certified family and community physician — useful for both acute problems and continuity of ongoing treatment.",
    image: florianopolisImg,
    imageAlt: "View of Lagoa da Conceição in Florianópolis at sunset, with the wooden bridge and the ocean behind",
    areas: [
      "Lagoa da Conceição",
      "Campeche and Morro das Pedras",
      "Jurerê Internacional",
      "Canasvieiras and Ingleses",
      "Centro and Trindade",
    ],
    pharmacies: ["Panvel", "Drogasil", "Droga Raia", "Farmácias São João"],
    audience: "digital nomads, remote workers, surfers and seasonal residents",
    localNotes: [
      "Long-stay visitors often need continuity of chronic treatment — we review your history and current medication before deciding whether a prescription is appropriate.",
      "Mental health support for anxiety, sleep problems and burnout is a frequent reason for consultation among remote workers.",
      "Consultations are available daily from 07:00 to 22:00 (Brasília time), with an average reply in 30 minutes.",
    ],
    emergencyNote:
      "For emergencies call 192 (SAMU). Hospital Baía Sul and Hospital Unimed in Florianópolis have private emergency care.",
  },
  {
    slug: "english-speaking-doctor-foz-do-iguacu",
    name: "Foz do Iguaçu",
    state: "Paraná",
    title: "English-Speaking Doctor in Foz do Iguaçu | Online Telemedicine | Rio Med Care",
    metaDescription:
      "Online consultation in English for travellers at Iguaçu Falls. Board-certified Brazilian family doctor, USD 50, receipts for travel insurance.",
    intro:
      "Visitors to the Iguaçu Falls often have tight itineraries and little time to find an English-speaking doctor. Rio Med Care offers online consultations from your hotel or lodge, with a board-certified family physician registered in Brazil.",
    image: fozDoIguacuImg,
    imageAlt: "Panoramic view of the Iguaçu Falls surrounded by tropical rainforest",
    areas: [
      "Hotels near the Cataratas do Iguaçu National Park",
      "Centro and Avenida Brasil",
      "Vila Yolanda and Jardim Polo Centro",
      "Lodges and resorts on the BR-469",
      "Triple-border area (Brazil, Argentina, Paraguay)",
    ],
    pharmacies: ["Farmácias Nissei", "Drogasil", "Droga Raia", "Panvel"],
    audience: "international tourists, tour groups and short-stay travellers",
    localNotes: [
      "Most consultations here involve stomach upsets, respiratory infections, insect bites and sun exposure during park visits.",
      "If you are crossing into Argentina or Paraguay, we provide documentation in English that travels with you.",
      "Digital prescriptions signed under Brazilian certification are valid in Brazilian pharmacies only.",
    ],
    emergencyNote:
      "In an emergency call 192 (SAMU) or go to the nearest hospital — Hospital Ministro Costa Cavalcanti in Foz do Iguaçu provides emergency care.",
  },
];

export const getCity = (slug: string) => cities.find((c) => c.slug === slug);
